import React, { useState, useContext } from 'react';
import AppContext from '../AppContext';
import { useLanguage } from '../contexts/LanguageContext';
import { BACKEND_URL } from '../config';
import { useModalClose } from '../hooks/useModalClose';
import { formatTaxRate } from '../utils/systemFeatures';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import gibbonsLogo from '../gibbons_text_logo.jpg';
import gibbonsFace from '../gibbons_face_updated.png';

const InvoiceGenerator = ({ orderId, onClose }) => {
  const { theme } = useContext(AppContext);
  const { t } = useLanguage();
  useModalClose(onClose);
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_customer_name: '',
    invoice_customer_address: '',
    invoice_customer_city: 'Guadalajara',
    invoice_customer_state: 'Jalisco',
    invoice_customer_zip: '',
    invoice_shipping_address: '',
    invoice_shipping_city: 'Guadalajara',
    invoice_shipping_state: 'Jalisco',
    invoice_shipping_zip: '',
    invoice_notes: '',
    invoice_payment_terms: 'A pagar a más tardar 15 días después de la entrega',
    invoice_shipping_cost: 0,
    invoice_received_date: null,
    invoice_received_by: ''
  });
  const [showAddItems, setShowAddItems] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // Fetch order data for invoice
  const fetchInvoiceData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${orderId}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setInvoiceData(data);

      // Pre-fill form if invoice already exists
      if (data.order.invoice_number) {
        setFormData({
          invoice_number: data.order.invoice_number || '',
          invoice_customer_name: data.order.invoice_customer_name || '',
          invoice_customer_address: data.order.invoice_customer_address || '',
          invoice_customer_city: data.order.invoice_customer_city || 'Guadalajara',
          invoice_customer_state: data.order.invoice_customer_state || 'Jalisco',
          invoice_customer_zip: data.order.invoice_customer_zip || '',
          invoice_shipping_address: data.order.invoice_shipping_address || '',
          invoice_shipping_city: data.order.invoice_shipping_city || 'Guadalajara',
          invoice_shipping_state: data.order.invoice_shipping_state || 'Jalisco',
          invoice_shipping_zip: data.order.invoice_shipping_zip || '',
          invoice_notes: data.order.invoice_notes || '',
          invoice_payment_terms: data.order.invoice_payment_terms || 'A pagar a más tardar 15 días después de la entrega',
          invoice_shipping_cost: data.order.invoice_shipping_cost || 0,
          invoice_received_date: data.order.invoice_received_date || null,
          invoice_received_by: data.order.invoice_received_by || ''
        });
      }
    } catch (error) {
      console.error('Error fetching invoice data:', error);
      alert(t('error') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (orderId) {
      fetchInvoiceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Fetch available products
  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const products = await response.json();
      setAvailableProducts(products.filter(p => p.active === 1));
    } catch (error) {
      console.error('Error fetching products:', error);
      alert(t('error') + ': ' + error.message);
    }
  };

  // Add selected items to order
  const addItemsToOrder = async () => {
    if (selectedItems.length === 0) {
      alert(t('pleaseSelectItems'));
      return;
    }

    setLoading(true);
    try {
      const items = selectedItems.map(item => ({
        id: item.product_id,
        userId: 1, // Default user
        qty: item.quantity,
        price: item.price,
        note: item.note || '',
        dontMake: false,
        selectedOptionId: null
      }));

      const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/send-items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Refresh invoice data to show new items
      await fetchInvoiceData();
      setShowAddItems(false);
      setSelectedItems([]);
      alert(t('itemsAddedSuccessfully'));
    } catch (error) {
      console.error('Error adding items:', error);
      alert(t('error') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Update invoice information in database
  const saveInvoiceData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      // Refresh invoice data
      await fetchInvoiceData();

      alert(t('success') + ': ' + result.message);
      return result.invoice_number;
    } catch (error) {
      console.error('Error saving invoice data:', error);
      alert(t('error') + ': ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Generate PDF matching the Gibbons invoice format
  const generatePDF = async () => {
    if (!invoiceData) {
      alert(t('error') + ': ' + t('loading'));
      return;
    }

    // Save invoice data first if not already saved
    let invoiceNumber = formData.invoice_number;
    if (!invoiceNumber || !invoiceData.order.invoice_generated_date) {
      invoiceNumber = await saveInvoiceData();
      if (!invoiceNumber) return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 15;

    // Header - GIBBONS logo with RED background (matching Gibbons brand)
    doc.setFillColor(211, 47, 47); // Red color #D32F2F
    doc.rect(10, 10, 105, 25, 'F');

    // Add Gibbons text logo on red background
    try {
      doc.addImage(gibbonsLogo, 'JPEG', 15, 13, 95, 18);
    } catch (error) {
      // Fallback to text if image fails to load
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(32);
      doc.setFont('helvetica', 'bold');
      doc.text('GIBBONS', 62.5, 26, { align: 'center' });
    }

    // Title - "Nota de Remisión"
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Nota de Remisión', 155, 26, { align: 'center' });

    yPos = 42;

    // Left side: Company information
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Cerveceria Gibbons S.A. de C.V.', 10, yPos);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100); // Gray text
    doc.setFontSize(9);
    doc.text('C. Escorza 848', 10, yPos + 5);
    doc.text('Col. Moderna, 44190, Guadalajara, Jalisco', 10, yPos + 9);
    doc.text('Tel/WhatsApp: +52 33 1050 9906', 10, yPos + 13);
    doc.text('Correo: pedidos@gibbons.mx', 10, yPos + 17);

    // Right side: Invoice number and date (right-aligned labels)
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);

    // eslint-disable-next-line no-unused-vars
            const fechaValue = new Date().toLocaleDateString('es-MX');
    // eslint-disable-next-line no-unused-vars
            const pedidoValue = invoiceNumber || `INV-${orderId}`;

    doc.text('Fecha', 200, yPos, { align: 'right' });
    doc.text('No. Pedido', 200, yPos + 5, { align: 'right' });

    yPos += 26;

    // Customer information section headers (teal/blue color)
    doc.setFillColor(51, 122, 183); // Blue color #337AB7
    doc.rect(10, yPos, 95, 8, 'F');
    doc.rect(110, yPos, 100, 8, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('A cuenta de', 14, yPos + 5.5);
    doc.text('Dirección de envío', 114, yPos + 5.5);

    yPos += 10;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    // Billing address
    const billingName = formData.invoice_customer_name || invoiceData.order.table_name || 'Cliente';
    const billingAddress = formData.invoice_customer_address || 'C. Colonias #79A';
    const billingCityState = `${formData.invoice_customer_city}, ${formData.invoice_customer_state} ${formData.invoice_customer_zip || '44600'}`;

    doc.text(billingName, 14, yPos);
    doc.text(billingAddress, 14, yPos + 4);
    doc.text(billingCityState, 14, yPos + 8);

    // Shipping address
    const shippingName = formData.invoice_customer_name || invoiceData.order.table_name || 'Cliente';
    const shippingAddress = formData.invoice_shipping_address || formData.invoice_customer_address || 'C. Colonias #79A';
    const shippingCityState = `${formData.invoice_shipping_city}, ${formData.invoice_shipping_state} ${formData.invoice_shipping_zip || '44600'}`;

    doc.text(shippingName, 114, yPos);
    doc.text(shippingAddress, 114, yPos + 4);
    doc.text(shippingCityState, 114, yPos + 8);

    yPos += 20;

    // Blue line separator
    doc.setDrawColor(51, 122, 183);
    doc.setLineWidth(1.5);
    doc.line(10, yPos, 200, yPos);

    yPos += 4;

    // Products table header (dark gray/black)
    doc.setFillColor(45, 45, 45); // Dark gray/black
    doc.rect(10, yPos, 200, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Descripción', 14, yPos + 5.5);
    doc.text('Precio', 125, yPos + 5.5, { align: 'center' });
    doc.text('Cantidad', 160, yPos + 5.5, { align: 'center' });
    doc.text('Importe', 200, yPos + 5.5, { align: 'right' });

    yPos += 10;

    // Add table rows with alternating light blue background
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    invoiceData.products.forEach((product, idx) => {
      if (yPos > pageHeight - 90) {
        doc.addPage();
        yPos = 20;
      }

      // Alternating light blue rows
      if (idx % 2 === 0) {
        doc.setFillColor(207, 226, 243); // Light blue #CFE2F3
        doc.rect(10, yPos - 3, 200, 7, 'F');
      }

      const unitPrice = parseFloat(product.price);
      const quantity = parseInt(product.quantity);
      const discount = parseFloat(product.discount || 0);
      const subtotalBeforeDiscount = unitPrice * quantity;
      const itemTotal = subtotalBeforeDiscount - discount;

      doc.text(product.product_name, 14, yPos);
      doc.text(`$${unitPrice.toFixed(2)}`, 125, yPos, { align: 'center' });
      doc.text(quantity.toString(), 160, yPos, { align: 'center' });
      doc.text(`$${itemTotal.toFixed(2)}`, 200, yPos, { align: 'right' });

      yPos += 7;
    });

    // Move to bottom section for conditions and totals
    yPos = Math.max(yPos + 10, pageHeight - 105);

    // Totals on the right side
    const subtotal = invoiceData.calculations.subtotal;
    const iva = invoiceData.calculations.iva;
    const shippingCost = parseFloat(formData.invoice_shipping_cost || 0);
    // eslint-disable-next-line no-unused-vars
    const total = subtotal + iva + shippingCost;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    let totalYPos = yPos;

    // Right column labels
    doc.text('Subtotal', 145, totalYPos);
    doc.text('$', 195, totalYPos, { align: 'right' });

    totalYPos += 5;
    doc.text('IVA', 145, totalYPos);
    doc.text('$', 195, totalYPos, { align: 'right' });

    totalYPos += 5;
    doc.text('Costo de Envío', 145, totalYPos);
    doc.text('$', 195, totalYPos, { align: 'right' });

    totalYPos += 5;
    doc.text('Anticipo', 145, totalYPos);
    doc.text('$', 195, totalYPos, { align: 'right' });

    totalYPos += 8;

    // Blue line separator before total
    doc.setDrawColor(51, 122, 183);
    doc.setLineWidth(1);
    doc.line(145, totalYPos - 2, 200, totalYPos - 2);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total a Pagar', 145, totalYPos);

    // Conditions section (left side)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('Condiciones', 10, yPos);

    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100); // Gray text
    const conditions = doc.splitTextToSize(formData.invoice_payment_terms, 130);
    doc.text(conditions, 10, yPos);

    // Signature lines at bottom
    yPos = pageHeight - 45;

    // Draw horizontal lines
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(10, yPos, 95, yPos);
    doc.line(115, yPos, 200, yPos);

    yPos += 4;
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('Entregado Por', 10, yPos);
    doc.text('Recibido Por', 115, yPos);

    yPos += 10;
    doc.line(10, yPos, 95, yPos);
    doc.line(115, yPos, 200, yPos);

    yPos += 4;
    doc.text('Firma', 10, yPos);
    doc.text('Firma', 115, yPos);

    // Received stamp if applicable (right side)
    if (formData.invoice_received_date) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(211, 47, 47); // Red color
      const receivedText = `RECIBIDO ${new Date(formData.invoice_received_date).toLocaleDateString('es-MX')}`;
      doc.text(receivedText, 155, yPos - 5, { align: 'center' });
    }

    yPos += 10;
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.line(10, yPos, 95, yPos);
    doc.line(115, yPos, 200, yPos);

    yPos += 4;
    doc.text('Fecha', 10, yPos);
    doc.text('Fecha', 115, yPos);

    // Footer with Gibbons face logo and text
    yPos = pageHeight - 15;

    // Add Gibbons face logo in footer (right side)
    try {
      doc.addImage(gibbonsFace, 'PNG', 175, yPos - 5, 20, 20);
    } catch (error) {
      console.error('Failed to add footer logo:', error);
    }

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('¡Gracias por su preferencia!', pageWidth / 2, yPos, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 122, 183);
    doc.text('Síganos en gibbons.mx', pageWidth / 2, yPos + 4, { align: 'center' });

    // Save the PDF
    const fileName = `Nota_Remision_${invoiceNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading && !invoiceData) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: theme?.colors?.text || '#000'
      }}>
        {t('loading')}
      </div>
    );
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        color: theme?.colors?.text || '#000',
        borderRadius: '8px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, color: theme?.colors?.primary || '#1976d2' }}>
            {t('createInvoice')}
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: theme?.colors?.error || '#d32f2f',
              color: theme?.colors?.textLight || '#fff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {t('close')}
          </button>
        </div>

        {invoiceData && (
          <>
            <div style={{
              backgroundColor: theme?.colors?.background || '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ marginTop: 0 }}>{t('invoiceDetails')}</h3>
              <p><strong>{t('orders')} ID:</strong> {orderId}</p>
              <p><strong>{t('tableName')}:</strong> {invoiceData.order.table_name}</p>
              {invoiceData.order.invoice_number && (
                <p><strong>{t('invoiceNumber')}:</strong> {invoiceData.order.invoice_number}</p>
              )}

              {/* Items List */}
              <div style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0 }}>{t('items')}:</h4>
                  <button
                    onClick={() => {
                      setShowAddItems(true);
                      fetchProducts();
                    }}
                    style={{
                      backgroundColor: theme?.colors?.secondary || '#666',
                      color: theme?.colors?.textLight || '#fff',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    ➕ {t('addItems')}
                  </button>
                </div>
                {invoiceData.products.length === 0 ? (
                  <div style={{
                    padding: '2rem',
                    textAlign: 'center',
                    border: `2px dashed ${theme?.colors?.border || '#ccc'}`,
                    borderRadius: '4px',
                    color: theme?.colors?.textSecondary || '#666'
                  }}>
                    {t('noItemsInOrder')}
                  </div>
                ) : (
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                    borderRadius: '4px',
                    padding: '0.5rem'
                  }}>
                    {invoiceData.products.map((product, idx) => {
                    const itemTotal = parseInt(product.quantity) * (parseFloat(product.price) - (parseFloat(product.discount) || 0));
                    return (
                      <div key={idx} style={{
                        padding: '0.5rem',
                        marginBottom: '0.5rem',
                        backgroundColor: theme?.colors?.surface || '#fff',
                        borderRadius: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.9rem'
                      }}>
                        <div style={{ flex: 1 }}>
                          <strong>{product.product_name}</strong>
                          <div style={{ color: theme?.colors?.textSecondary || '#666', fontSize: '0.85rem' }}>
                            {product.quantity} x ${parseFloat(product.price).toFixed(2)}
                            {product.discount > 0 && ` (-$${parseFloat(product.discount).toFixed(2)})`}
                          </div>
                        </div>
                        <div style={{ fontWeight: 'bold', color: theme?.colors?.primary || '#1976d2' }}>
                          ${itemTotal.toFixed(2)}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                )}
              </div>

              {/* Totals Summary */}
              <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: `2px solid ${theme?.colors?.border || '#ccc'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span>{t('subtotal')}:</span>
                  <strong>${invoiceData.calculations.subtotal.toFixed(2)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span>{t('iva')} ({formatTaxRate((invoiceData.calculations.ivaRate || 0) * 100)}%):</span>
                  <strong>${invoiceData.calculations.iva.toFixed(2)}</strong>
                </div>
                {invoiceData.calculations.shippingCost > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span>{t('shippingCost')}:</span>
                    <strong>${invoiceData.calculations.shippingCost.toFixed(2)}</strong>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '0.5rem',
                  paddingTop: '0.5rem',
                  borderTop: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  fontSize: '1.1rem'
                }}>
                  <strong>{t('total')}:</strong>
                  <strong style={{ color: theme?.colors?.primary || '#1976d2' }}>
                    ${invoiceData.calculations.total.toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div>
                <h3>{t('billingAddress')}</h3>
                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                    {t('customerName')}
                  </label>
                  <input
                    type="text"
                    name="invoice_customer_name"
                    value={formData.invoice_customer_name}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                      backgroundColor: theme?.colors?.surface || '#fff',
                      color: theme?.colors?.text || '#000'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                    {t('customerAddress')}
                  </label>
                  <input
                    type="text"
                    name="invoice_customer_address"
                    value={formData.invoice_customer_address}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                      backgroundColor: theme?.colors?.surface || '#fff',
                      color: theme?.colors?.text || '#000'
                    }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      {t('customerCity')}
                    </label>
                    <input
                      type="text"
                      name="invoice_customer_city"
                      value={formData.invoice_customer_city}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      {t('customerState')}
                    </label>
                    <input
                      type="text"
                      name="invoice_customer_state"
                      value={formData.invoice_customer_state}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      {t('customerZip')}
                    </label>
                    <input
                      type="text"
                      name="invoice_customer_zip"
                      value={formData.invoice_customer_zip}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3>{t('shippingAddress')}</h3>
                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                    {t('customerAddress')}
                  </label>
                  <input
                    type="text"
                    name="invoice_shipping_address"
                    value={formData.invoice_shipping_address}
                    onChange={handleInputChange}
                    placeholder={t('customerAddress')}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                      backgroundColor: theme?.colors?.surface || '#fff',
                      color: theme?.colors?.text || '#000'
                    }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      {t('customerCity')}
                    </label>
                    <input
                      type="text"
                      name="invoice_shipping_city"
                      value={formData.invoice_shipping_city}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      {t('customerState')}
                    </label>
                    <input
                      type="text"
                      name="invoice_shipping_state"
                      value={formData.invoice_shipping_state}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      {t('customerZip')}
                    </label>
                    <input
                      type="text"
                      name="invoice_shipping_zip"
                      value={formData.invoice_shipping_zip}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                    {t('invoiceNumber')}
                  </label>
                  <input
                    type="text"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleInputChange}
                    placeholder={`${orderId}`}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                      backgroundColor: theme?.colors?.surface || '#fff',
                      color: theme?.colors?.text || '#000'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                    {t('shippingCost')}
                  </label>
                  <input
                    type="number"
                    name="invoice_shipping_cost"
                    value={formData.invoice_shipping_cost}
                    onChange={handleInputChange}
                    onWheel={(e) => e.target.blur()}
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                      backgroundColor: theme?.colors?.surface || '#fff',
                      color: theme?.colors?.text || '#000'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                  {t('paymentTerms')}
                </label>
                <input
                  type="text"
                  name="invoice_payment_terms"
                  value={formData.invoice_payment_terms}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                    backgroundColor: theme?.colors?.surface || '#fff',
                    color: theme?.colors?.text || '#000'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                  {t('invoiceNotes')}
                </label>
                <textarea
                  name="invoice_notes"
                  value={formData.invoice_notes}
                  onChange={handleInputChange}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                    backgroundColor: theme?.colors?.surface || '#fff',
                    color: theme?.colors?.text || '#000',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={saveInvoiceData}
                disabled={loading}
                style={{
                  backgroundColor: theme?.colors?.secondary || '#666',
                  color: theme?.colors?.textLight || '#fff',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {t('save')} {t('invoiceDetails')}
              </button>
              <button
                onClick={generatePDF}
                disabled={loading}
                style={{
                  backgroundColor: theme?.colors?.primary || '#1976d2',
                  color: theme?.colors?.textLight || '#fff',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  fontWeight: 'bold'
                }}
              >
                {t('downloadInvoice')} (PDF)
              </button>
            </div>
          </>
        )}
      </div>

      {/* Add Items Modal */}
      {showAddItems && (
        <div onClick={() => {
          setShowAddItems(false);
          setSelectedItems([]);
        }} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: theme?.colors?.surface || '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, color: theme?.colors?.text || '#000' }}>
              {t('addItems')}
            </h3>

            {/* Product Selection */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: theme?.colors?.text || '#000'
              }}>
                {t('selectProduct')}:
              </label>
              <select
                onChange={(e) => {
                  const product = availableProducts.find(p => p.id === parseInt(e.target.value));
                  if (product) {
                    setSelectedItems([...selectedItems, {
                      product_id: product.id,
                      product_name: product.name,
                      price: product.price,
                      quantity: 1,
                      note: ''
                    }]);
                    e.target.value = '';
                  }
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000'
                }}
              >
                <option value="">{t('selectProduct')}...</option>
                {availableProducts.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name} - ${parseFloat(product.price).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            {/* Selected Items */}
            {selectedItems.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ color: theme?.colors?.text || '#000' }}>{t('selectedItems')}:</h4>
                {selectedItems.map((item, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    padding: '0.5rem',
                    backgroundColor: theme?.colors?.background || '#f5f5f5',
                    borderRadius: '4px',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1, color: theme?.colors?.text || '#000' }}>
                      <strong>{item.product_name}</strong>
                      <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
                        ${parseFloat(item.price).toFixed(2)}
                      </div>
                    </div>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => {
                        const newItems = [...selectedItems];
                        newItems[idx].quantity = parseInt(e.target.value) || 1;
                        setSelectedItems(newItems);
                      }}
                      onWheel={(e) => e.target.blur()}
                      style={{
                        width: '60px',
                        padding: '0.25rem',
                        borderRadius: '4px',
                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                        textAlign: 'center'
                      }}
                    />
                    <button
                      onClick={() => {
                        setSelectedItems(selectedItems.filter((_, i) => i !== idx));
                      }}
                      style={{
                        backgroundColor: theme?.colors?.error || '#d32f2f',
                        color: '#fff',
                        border: 'none',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddItems(false);
                  setSelectedItems([]);
                }}
                style={{
                  backgroundColor: theme?.colors?.secondary || '#666',
                  color: theme?.colors?.textLight || '#fff',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={addItemsToOrder}
                disabled={selectedItems.length === 0}
                style={{
                  backgroundColor: selectedItems.length > 0 ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.border || '#ccc'),
                  color: theme?.colors?.textLight || '#fff',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: selectedItems.length > 0 ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold'
                }}
              >
                {t('addItems')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceGenerator;
